/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { DIALOG_IDS } from 'constants';

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { Search } from 'carbon-addons-iot-react';

import { reduxDialog } from '../../containers/ReduxDialog/ReduxDialog';

import { formatAppError } from 'Utils';
import { BaseModal, CommonTable } from 'Common';
import { SortDirection } from 'CommonTypes';

const propTypes = {
  payload: PropTypes.shape({
    /** an array of alert ids whose status you should change */
    alertIds: PropTypes.arrayOf(PropTypes.number),
    /** an array of available owners  */
    availableOwners: PropTypes.arrayOf(
      PropTypes.shape({ email: PropTypes.string, name: PropTypes.string })
    ),
    /** Callback called with new owner and an array of alertIds to update */
    onChangeOwner: PropTypes.func.isRequired,
  }).isRequired,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
};

const defaultProps = {
  isOpen: false,
  onClose: null,
};

const messages = defineMessages({
  title: 'Bulk Assign Alerts to User',
  helpText: 'Choose who you would like to assign the alerts to.',
  searchText: 'Search for a user',
  name: 'Name',
  email: 'Email',
  errorUpdating: 'Error updating alerts',
  clearSearch: 'Clear search',
  search: 'Search',
  nodata: 'No data matches the filter ',
  close: 'Close notification',
});

const ChangeAlertOwnerModal = ({
  payload: { availableOwners, alertIds, onChangeOwner },
  onClose,
  intl: { formatMessage },
  ...others
}) => {
  const [error, setError] = useState();
  const [selectedOwner, setSelectedOwner] = useState();
  const [isSubmitting, setIsSubmitting] = useState();
  const [search, setSearch] = useState('');

  // BaseModal can only take a single string as an error message so define the formatted error here
  // then concat it in the modal
  let errorMessage;
  if (error) {
    errorMessage = { ...formatAppError(messages.errorUpdating, error, formatMessage) };
  }

  const handleSubmit = () => {
    setIsSubmitting(true);
    onChangeOwner(selectedOwner, alertIds)
      .then(() => {
        // if no error is thrown close the dialog
        onClose();
      })
      .catch(errorResponse => {
        setError(errorResponse);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <BaseModal
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...others}
      className="alert-owner-modal"
      header={{
        helpText: formatMessage(messages.helpText),
        title: formatMessage(messages.title),
      }}
      size="sm"
      onClose={onClose}
      footer={{ isPrimaryButtonDisabled: !selectedOwner || isSubmitting }}
      onSubmit={handleSubmit}
      error={error ? `${errorMessage.title} - ${errorMessage.subtitle}` : null}>
      <Search
        closeButtonLabelText={formatMessage(messages.clearSearch)}
        defaultValue={search}
        id="search-1"
        labelText={formatMessage(messages.search)}
        name="search-1"
        onChange={event => {
          setSelectedOwner();
          setSearch(event?.currentTarget?.value);
        }}
        placeholder={formatMessage(messages.searchText)}
        size="xl"
        type="text"
      />
      <CommonTable
        id="change-alert-owner-modal-table"
        style={{ paddingTop: '2rem' }}
        columns={[
          { id: 'name', name: formatMessage(messages.name), isSortable: true },
          {
            id: 'email',
            name: formatMessage(messages.email),
            isSortable: true,
          },
        ]}
        data={availableOwners
          .filter(owner => owner.email.includes(search) || owner.name.includes(search))
          .map(owner => ({ id: owner.email, values: owner }))}
        options={{ hasRowSelection: 'single', hasPagination: true }}
        view={{
          table: {
            sort: { columnId: 'email', direction: SortDirection.ASC },
            emptyState: { message: formatMessage(messages.nodata) },
          },
        }}
        actions={{
          table: {
            onRowSelected: email => {
              setSelectedOwner(email);
            },
          },
        }}
      />
    </BaseModal>
  );
};

ChangeAlertOwnerModal.propTypes = propTypes;
ChangeAlertOwnerModal.defaultProps = defaultProps;

export default injectIntl(
  reduxDialog({ name: DIALOG_IDS.CHANGE_ALERT_OWNER_MODAL })(ChangeAlertOwnerModal)
);
